import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  User, LogOut, Building2, Hotel, Calendar, Clock, Key, Lock, 
  ChevronDown, Settings, Shield
} from 'lucide-react';
import { toast } from 'sonner';

export default function TopHeader({ 
  user, 
  onLogout, 
  selectedCompany, 
  onCompanyChange,
  activeHotel,
  onHotelChange,
  selectedFinancialYear,
  onFinancialYearChange
}) {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [lastLogin, setLastLogin] = useState(null);
  
  // Dialogs
  const [changePinOpen, setChangePinOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [pinData, setPinData] = useState({ current_pin: '', new_pin: '', confirm_pin: '' });
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);

  // Determine what selectors to show based on role
  const showCompanySelector = ['company_admin', 'finance_admin'].includes(user?.role);
  const showHotelSelector = ['company_admin', 'hotel_admin', 'manager', 'front_office', 'restaurant_staff', 'service_manager'].includes(user?.role);
  const showFinancialYearSelector = ['company_admin', 'finance_admin', 'hotel_admin'].includes(user?.role);

  useEffect(() => {
    loadData();
    loadLastLogin();
  }, [user]);

  useEffect(() => {
    if (selectedCompany?.id) {
      loadFinancialYears(selectedCompany.id);
    }
  }, [selectedCompany]);

  const loadData = async () => {
    try {
      // Load companies for company-level users
      if (showCompanySelector && user) {
        const companyIds = user.company_ids || (user.company_id ? [user.company_id] : []);
        if (companyIds.length > 0) {
          const res = await api.get('/companies');
          const userCompanies = res.data.filter(c => companyIds.includes(c.id));
          setCompanies(userCompanies);
          
          // Auto-select first company if none selected
          if (!selectedCompany && userCompanies.length > 0 && onCompanyChange) {
            onCompanyChange(userCompanies[0]);
          }
        }
      }

      // Load hotels
      if (showHotelSelector && user) {
        try {
          let hotelsList = [];
          if (user.role === 'super_admin') {
            const hotelsRes = await api.get('/super-admin/hotels');
            hotelsList = hotelsRes.data || [];
          } else if (user.role === 'company_admin' || user.role === 'finance_admin') {
            // For company-level users, get hotels from their company
            const companyIds = user.company_ids || (user.company_id ? [user.company_id] : []);
            if (companyIds.length > 0) {
              // Get hotels for the first/primary company
              const hotelsRes = await api.get(`/companies/${companyIds[0]}/hotels`);
              hotelsList = hotelsRes.data || [];
            }
          } else {
            // For hotel-level users, use the user/hotels endpoint
            const hotelsRes = await api.get('/user/hotels');
            hotelsList = hotelsRes.data?.hotels || [];
          }
          setHotels(hotelsList);
          
          // Auto-select first hotel if none selected
          if (!activeHotel && hotelsList.length > 0 && onHotelChange) {
            onHotelChange(hotelsList[0]);
          }
        } catch (e) {
          console.log('Could not load hotels');
        }
      }
    } catch (error) {
      console.error('Error loading header data:', error);
    }
  };

  const loadFinancialYears = async (companyId) => {
    try {
      const res = await api.get(`/finance/financial-years?company_id=${companyId}`);
      const years = res.data || [];
      setFinancialYears(years);
      
      // Auto-select financial year if none selected and years exist
      if (!selectedFinancialYear && years.length > 0 && onFinancialYearChange) {
        // Try to find active/current year, otherwise use first
        const activeYear = years.find(fy => fy.is_active || fy.status === 'active') || years[0];
        onFinancialYearChange(activeYear);
      }
    } catch (e) {
      setFinancialYears([]);
    }
  };

  const loadLastLogin = () => {
    // Get last login from localStorage
    const lastLoginStr = localStorage.getItem('lastLogin');
    if (lastLoginStr) {
      setLastLogin(new Date(lastLoginStr));
    }
    // Update last login time
    localStorage.setItem('lastLogin', new Date().toISOString());
  };

  const handleSetPin = async () => {
    if (pinData.new_pin !== pinData.confirm_pin) {
      toast.error('PINs do not match');
      return;
    }
    if (pinData.new_pin.length < 4 || pinData.new_pin.length > 6) {
      toast.error('PIN must be 4-6 digits');
      return;
    }
    setSaving(true);
    try {
      await api.post('/auth/set-pin', { 
        current_pin: pinData.current_pin || null,
        new_pin: pinData.new_pin 
      });
      toast.success('PIN set successfully');
      setChangePinOpen(false);
      setPinData({ current_pin: '', new_pin: '', confirm_pin: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set PIN');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSaving(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully');
      setChangePasswordOpen(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const formatLastLogin = (date) => {
    if (!date) return 'First login';
    return new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: 'bg-red-100 text-red-700 border-red-200',
      company_admin: 'bg-purple-100 text-purple-700 border-purple-200',
      finance_admin: 'bg-green-100 text-green-700 border-green-200',
      hotel_admin: 'bg-blue-100 text-blue-700 border-blue-200',
      manager: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      front_office: 'bg-amber-100 text-amber-700 border-amber-200',
    };
    return colors[role] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const formatRoleName = (role) => {
    return role?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'User';
  };

  return (
    <>
      <div className="h-14 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 border-b border-slate-600 px-4 flex items-center justify-between shadow-md sticky top-0 z-50">
        {/* Left side - Selectors */}
        <div className="flex items-center gap-3">
          {/* Company Selector */}
          {showCompanySelector && companies.length > 0 && (
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-purple-300" />
              <Select 
                value={selectedCompany?.id || ''} 
                onValueChange={(id) => {
                  const company = companies.find(c => c.id === id);
                  if (company && onCompanyChange) onCompanyChange(company);
                }}
              >
                <SelectTrigger className="w-[200px] h-9 text-sm bg-slate-700/50 border-slate-600 text-white hover:bg-slate-600/50">
                  <SelectValue placeholder="Select Company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(company => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Hotel Selector */}
          {showHotelSelector && hotels.length > 0 && (
            <div className="flex items-center gap-2">
              <Hotel className="h-4 w-4 text-cyan-300" />
              <Select 
                value={activeHotel?.id || ''} 
                onValueChange={(id) => {
                  const hotel = hotels.find(h => h.id === id);
                  if (hotel && onHotelChange) onHotelChange(hotel);
                }}
              >
                <SelectTrigger className="w-[180px] h-9 text-sm bg-slate-700/50 border-slate-600 text-white hover:bg-slate-600/50">
                  <SelectValue placeholder="Select Hotel" />
                </SelectTrigger>
                <SelectContent>
                  {hotels.map(hotel => (
                    <SelectItem key={hotel.id} value={hotel.id}>
                      {hotel.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Financial Year Selector */}
          {showFinancialYearSelector && financialYears.length > 0 && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-emerald-300" />
              <Select 
                value={selectedFinancialYear?.id || ''} 
                onValueChange={(id) => {
                  const fy = financialYears.find(f => f.id === id);
                  if (fy && onFinancialYearChange) onFinancialYearChange(fy);
                }}
              >
                <SelectTrigger className="w-[150px] h-9 text-sm bg-slate-700/50 border-slate-600 text-white hover:bg-slate-600/50">
                  <SelectValue placeholder="Financial Year" />
                </SelectTrigger>
                <SelectContent>
                  {financialYears.map(fy => (
                    <SelectItem key={fy.id} value={fy.id}>
                      FY {fy.name || fy.year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Right side - Last Login & Profile */}
        <div className="flex items-center gap-4">
          {/* Last Login */}
          {lastLogin && (
            <div className="hidden md:flex items-center gap-2 text-xs text-slate-300">
              <Clock className="h-3 w-3" />
              <span>Last login: {formatLastLogin(lastLogin)}</span>
            </div>
          )}

          {/* User Profile Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 h-9 px-3 hover:bg-slate-600/50">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-sm font-medium shadow-md">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="hidden sm:flex flex-col items-start">
                  <span className="text-sm font-medium text-white">{user?.name || 'User'}</span>
                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${getRoleBadgeColor(user?.role)}`}>
                    {formatRoleName(user?.role)}
                  </Badge>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-300" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="font-medium">{user?.name}</span>
                  <span className="text-xs text-gray-500 font-normal">{user?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setChangePinOpen(true)}>
                <Key className="h-4 w-4 mr-2" />
                Set / Change PIN
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setChangePasswordOpen(true)}>
                <Lock className="h-4 w-4 mr-2" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={onLogout}
                className="text-red-600 focus:text-red-600 focus:bg-red-50"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Set PIN Dialog */}
      <Dialog open={changePinOpen} onOpenChange={setChangePinOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-primary" />
              Set / Change PIN
            </DialogTitle>
            <DialogDescription>
              Set a 4-6 digit PIN for quick access
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current PIN (if already set)</Label>
              <Input
                type="password"
                maxLength={6}
                value={pinData.current_pin}
                onChange={(e) => setPinData({...pinData, current_pin: e.target.value.replace(/\D/g, '')})}
                placeholder="Leave empty if first time"
              />
            </div>
            <div className="space-y-2">
              <Label>New PIN *</Label>
              <Input
                type="password"
                maxLength={6}
                value={pinData.new_pin}
                onChange={(e) => setPinData({...pinData, new_pin: e.target.value.replace(/\D/g, '')})}
                placeholder="4-6 digits"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm PIN *</Label>
              <Input
                type="password"
                maxLength={6}
                value={pinData.confirm_pin}
                onChange={(e) => setPinData({...pinData, confirm_pin: e.target.value.replace(/\D/g, '')})}
                placeholder="Re-enter PIN"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleSetPin} disabled={saving} className="flex-1">
                {saving ? 'Saving...' : 'Save PIN'}
              </Button>
              <Button variant="outline" onClick={() => setChangePinOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              Change Password
            </DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new one
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Password *</Label>
              <Input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                placeholder="Enter current password"
              />
            </div>
            <div className="space-y-2">
              <Label>New Password *</Label>
              <Input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                placeholder="Minimum 6 characters"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password *</Label>
              <Input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                placeholder="Re-enter new password"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleChangePassword} disabled={saving} className="flex-1">
                {saving ? 'Changing...' : 'Change Password'}
              </Button>
              <Button variant="outline" onClick={() => setChangePasswordOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
